import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { RefreshCw, Download, Boxes } from 'lucide-react';
import * as XLSX from 'xlsx';
import { SkeletonRows, NoResults, ErrorBanner } from './shell/carbonStates';
import Div60AtsReport from './Div60AtsReport'; // CHA-369 — ATS report with images

// ============================================================================
// INVENTORY DASHBOARD - On Hand & WIP by Warehouse and Season
// ============================================================================
// CHA-275 — Carbon pass (Modules concept, Orders table anatomy). Presentation
// only: same getInventoryData callable, same filters/aggregation/exports and
// the exact same numbers; chrome restyled to carbon-* tokens (radius 0, Plex,
// per-tile-border KPI strips, mono grid tables, ink filter chips).
export default function InventoryDashboard() {
  // Data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Filter state
  const [selectedCompanies, setSelectedCompanies] = useState([]); // Multi-select buttons
  const [selectedDivision, setSelectedDivision] = useState(null); // Single dropdown
  const [selectedWarehouses, setSelectedWarehouses] = useState([]); // Multi-select
  const [selectedSeasons, setSelectedSeasons] = useState([]); // Multi-select
  const [excludeAL, setExcludeAL] = useState(false); // Exclude seasons starting with AL
  const [viewMode, setViewMode] = useState('units'); // 'units' or 'dollars'
  const [activeSection, setActiveSection] = useState('onhand'); // 'onhand' or 'wip'

  // Format units (thousands with K, millions with M)
  const formatUnits = (value) => {
    if (value === null || value === undefined) return '0';
    const num = Number(value) || 0;
    if (num === 0) return '0';
    const millions = num / 1000000;
    if (Math.abs(millions) >= 1) {
      return `${millions.toFixed(2)}M`;
    }
    const thousands = num / 1000;
    if (Math.abs(thousands) >= 1) {
      return `${thousands.toFixed(1)}K`;
    }
    return new Intl.NumberFormat('en-US').format(Math.round(num));
  };

  // Format currency in $M with 2 decimal places
  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === 0) return '$0.00M';
    const num = Number(value) || 0;
    const millions = num / 1000000;
    return `$${millions.toFixed(2)}M`;
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const functions = getFunctions();
      const getInventoryData = httpsCallable(functions, 'getInventoryData', { timeout: 150000 });

      const result = await getInventoryData({
        selectedCompanies: selectedCompanies.length > 0 ? selectedCompanies : null,
        selectedDivision,
        selectedWarehouses: selectedWarehouses.length > 0 ? selectedWarehouses : null,
        selectedSeasons: selectedSeasons.length > 0 ? selectedSeasons : null,
        excludeAL,
      });

      setData(result.data);
    } catch (err) {
      console.error('Error fetching inventory data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanies, selectedDivision, selectedWarehouses, selectedSeasons, excludeAL]);

  // Load data on mount and when filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Clear all filters
  const clearFilters = () => {
    setSelectedCompanies([]);
    setSelectedDivision(null);
    setSelectedWarehouses([]);
    setSelectedSeasons([]);
  };

  // Check if any filters are active
  const hasActiveFilters = selectedCompanies.length > 0 || selectedDivision !== null || selectedWarehouses.length > 0 || selectedSeasons.length > 0;

  // Handle warehouse click - multi-select toggle
  const handleWarehouseClick = (wh) => {
    setSelectedWarehouses(prev =>
      prev.includes(wh)
        ? prev.filter(w => w !== wh)
        : [...prev, wh]
    );
  };

  // Handle season click - multi-select toggle
  const handleSeasonClick = (season) => {
    setSelectedSeasons(prev =>
      prev.includes(season)
        ? prev.filter(s => s !== season)
        : [...prev, season]
    );
  };

  // Handle company click - multi-select toggle
  const handleCompanyClick = (comp) => {
    setSelectedCompanies(prev =>
      prev.includes(comp)
        ? prev.filter(c => c !== comp)  // Remove if already selected
        : [...prev, comp]               // Add if not selected
    );
  };

  // Get totals
  const getTotals = () => {
    if (!data?.warehouseSummary) return { qoh: 0, wip: 0, open: 0, pick: 0, ats: 0, available: 0, qohValue: 0, wipValue: 0 };
    return data.warehouseSummary.reduce((acc, w) => ({
      qoh: acc.qoh + (w.Total_QOH || 0),
      wip: acc.wip + (w.Total_WIP || 0),
      open: acc.open + (w.Total_Open || 0),
      pick: acc.pick + (w.Total_Pick || 0),
      ats: acc.ats + (w.ATS || 0),
      available: acc.available + (w.Available_To_Sell || 0),
      qohValue: acc.qohValue + (w.QOH_Value || 0),
      wipValue: acc.wipValue + (w.WIP_Value || 0),
    }), { qoh: 0, wip: 0, open: 0, pick: 0, ats: 0, available: 0, qohValue: 0, wipValue: 0 });
  };

  const totals = getTotals();
  const fmt = viewMode === 'dollars' ? formatCurrency : formatUnits;

  // Carbon shorthands (presentation only)
  const TH = 'px-3 py-2 font-plex-mono text-[10px] tracking-[0.12em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border bg-carbon-gray-10';
  const MONO = 'font-plex-mono text-[11.5px]';
  const chipCls = (on) => `px-2.5 py-1 font-plex-mono text-[10.5px] tracking-[0.04em] uppercase font-medium border ${
    on ? 'bg-carbon-ink text-carbon-white border-carbon-ink' : 'bg-carbon-white text-carbon-gray-70 border-carbon-gray-30 hover:bg-carbon-gray-10'
  }`;

  // KPI tile (per-tile borders — never a gray-gap grid with odd tile counts)
  const KpiTile = ({ label, value, color }) => (
    <div className="bg-carbon-white px-4 py-3.5 border-r border-t border-carbon-border min-w-0">
      <div className="font-plex-mono text-[9.5px] tracking-[0.12em] uppercase text-carbon-gray-50">{label}</div>
      <div className="mt-1.5">
        {loading ? (
          <div className="h-6 w-16 bg-carbon-gray-10 animate-pulse" />
        ) : (
          <span className="font-plex-mono text-xl font-semibold" style={{ color }}>{value}</span>
        )}
      </div>
    </div>
  );

  // In-flight refresh overlay (data already on screen — inline spinner is the
  // sanctioned state for in-flight actions; first load uses skeletons below).
  const RefetchOverlay = () => (
    loading && data ? (
      <div className="absolute inset-0 bg-carbon-white/60 flex items-center justify-center z-10">
        <RefreshCw className="h-5 w-5 text-carbon-blue-60 animate-spin" />
      </div>
    ) : null
  );

  return (
    <div className="font-plex text-carbon-ink">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 flex items-center justify-center bg-carbon-gray-10 text-carbon-ink">
            <Boxes size={18} />
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-[-0.01em] m-0">Inventory</h1>
            <div className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mt-0.5">
              On hand &amp; WIP · by warehouse &amp; season
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Units / $ segmented control */}
          <div className="flex border border-carbon-gray-30">
            <button
              onClick={() => setViewMode('units')}
              className={`px-3 py-1.5 font-plex-mono text-[10.5px] uppercase font-medium ${
                viewMode === 'units' ? 'bg-carbon-ink text-carbon-white' : 'bg-carbon-white text-carbon-gray-70 hover:bg-carbon-gray-10'
              }`}
            >
              Units
            </button>
            <button
              onClick={() => setViewMode('dollars')}
              className={`px-3 py-1.5 font-plex-mono text-[10.5px] uppercase font-medium border-l border-carbon-gray-30 ${
                viewMode === 'dollars' ? 'bg-carbon-ink text-carbon-white' : 'bg-carbon-white text-carbon-gray-70 hover:bg-carbon-gray-10'
              }`}
            >
              $
            </button>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80 disabled:opacity-50"
          >
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-carbon-white border border-carbon-border px-[18px] py-2.5 mb-5">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Company Filter */}
          <span className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50">Company</span>
          <div className="flex items-center gap-1 flex-wrap">
            <button onClick={() => setSelectedCompanies([])} className={chipCls(selectedCompanies.length === 0)}>
              All
            </button>
            {['IMG', 'BWI', 'INNOVATION', 'META', 'IMG APPAREL'].map((comp) => (
              <button key={comp} onClick={() => handleCompanyClick(comp)} className={chipCls(selectedCompanies.includes(comp))}>
                {comp}
              </button>
            ))}
          </div>

          <div className="h-5 w-px bg-carbon-border"></div>

          {/* Division Dropdown */}
          <select
            value={selectedDivision || ''}
            onChange={(e) => setSelectedDivision(e.target.value || null)}
            className="px-2 py-1 border border-carbon-gray-30 bg-carbon-white text-carbon-ink text-xs"
          >
            <option value="">All Divisions</option>
            {data?.divisions?.map(div => (
              <option key={div} value={div}>{div}</option>
            ))}
          </select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="font-plex-mono text-[10.5px] tracking-[0.08em] uppercase text-carbon-red-60 hover:bg-carbon-gray-10 px-2 py-1 ml-auto"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Second row: Warehouse and Season multi-select */}
        <div className="flex items-center gap-3 flex-wrap mt-2.5 pt-2.5 border-t border-carbon-gray-10">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mr-1">Warehouse</span>
            <button onClick={() => setSelectedWarehouses([])} className={chipCls(selectedWarehouses.length === 0)}>
              All
            </button>
            {data?.warehouseSummary?.slice(0, 10).map(w => (
              <button key={w.Warehouse} onClick={() => handleWarehouseClick(w.Warehouse)} className={chipCls(selectedWarehouses.includes(w.Warehouse))}>
                {w.Warehouse}
              </button>
            ))}
          </div>

          <div className="h-5 w-px bg-carbon-border"></div>

          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mr-1">Season</span>
            <button onClick={() => setSelectedSeasons([])} className={chipCls(selectedSeasons.length === 0)}>
              All
            </button>
            {data?.seasonSummary?.slice(0, 12).map(s => (
              <button key={s.Season} onClick={() => handleSeasonClick(s.Season)} className={chipCls(selectedSeasons.includes(s.Season))}>
                {s.Season}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Error State — recoverable, retry re-runs the same fetch */}
      {error && (
        <ErrorBanner what="Inventory data failed to load." staleNote={error} onRetry={fetchData} />
      )}

      {/* Section Tabs */}
      <div className="bg-carbon-white border border-carbon-border overflow-hidden">
        <div className="flex border-b border-carbon-border">
          {[
            { key: 'onhand', label: 'On Hand' },
            { key: 'wip', label: 'WIP' },
            { key: 'div60ats', label: 'Div 60 ATS' } // CHA-369 — ATS report with images
          ].map((t) => {
            const on = activeSection === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveSection(t.key)}
                className={`px-[18px] py-2.5 text-[13.5px] -mb-px border-b-2 ${
                  on
                    ? 'text-carbon-ink font-semibold border-carbon-blue-60'
                    : 'text-carbon-gray-50 border-transparent hover:text-carbon-ink'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* ============================================================ */}
        {/* ON HAND SECTION                                              */}
        {/* ============================================================ */}
        {activeSection === 'onhand' && (
          <div className="p-4">
            {/* KPI strip — per-tile borders */}
            <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] border-l border-b border-carbon-border mb-5">
              <KpiTile label="Total QOH" value={formatUnits(totals.qoh)} color="#161616" />
              <KpiTile label="Open" value={formatUnits(totals.open)} color="#BA4E00" />
              <KpiTile label="Pick" value={formatUnits(totals.pick)} color="#6929C4" />
              <KpiTile label="ATS" value={formatUnits(totals.ats)} color="#24A148" />
              <KpiTile label="QOH Value" value={formatCurrency(totals.qohValue)} color="#161616" />
            </div>

            {loading && !data ? (
              <div className="border border-carbon-border">
                <SkeletonRows rows={6} />
              </div>
            ) : (
            <>
            {/* Tables Row */}
            <div className="grid grid-cols-2 gap-5">
              {/* By Warehouse */}
              <div className="bg-carbon-white border border-carbon-border overflow-hidden relative">
                <RefetchOverlay />
                <div className="px-[18px] py-2.5 border-b border-carbon-border bg-carbon-gray-10">
                  <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">On hand by warehouse</span>
                </div>
                <div className="overflow-auto h-96">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 z-5">
                      <tr>
                        <th className={`${TH} text-left`}>Warehouse</th>
                        <th className={`${TH} text-right`}>Styles</th>
                        {viewMode === 'units' ? (
                          <>
                            <th className={`${TH} text-right`}>QOH</th>
                            <th className={`${TH} text-right`}>Open</th>
                            <th className={`${TH} text-right`}>Pick</th>
                            <th className={`${TH} text-right`}>ATS</th>
                          </>
                        ) : (
                          <th className={`${TH} text-right`}>QOH Value</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {data?.warehouseSummary?.map((row, idx) => {
                        const isSelected = selectedWarehouses.includes(row.Warehouse);
                        return (
                          <tr
                            key={row.Warehouse || idx}
                            onClick={() => handleWarehouseClick(row.Warehouse)}
                            className={`border-b border-carbon-gray-10 cursor-pointer ${isSelected ? 'bg-carbon-blue-10' : 'hover:bg-carbon-gray-10'}`}
                          >
                            <td className={`px-3 py-2 ${MONO} font-medium text-carbon-ink`}>{row.Warehouse || '(blank)'}</td>
                            <td className={`px-3 py-2 text-right ${MONO} text-carbon-gray-70`}>{formatUnits(row.Style_Count)}</td>
                            {viewMode === 'units' ? (
                              <>
                                <td className={`px-3 py-2 text-right ${MONO} text-carbon-gray-70`}>{formatUnits(row.Total_QOH)}</td>
                                <td className={`px-3 py-2 text-right ${MONO} text-carbon-gray-70`}>{formatUnits(row.Total_Open)}</td>
                                <td className={`px-3 py-2 text-right ${MONO} text-carbon-gray-70`}>{formatUnits(row.Total_Pick)}</td>
                                <td className={`px-3 py-2 text-right ${MONO} font-medium`} style={{ color: '#24A148' }}>{formatUnits(row.ATS)}</td>
                              </>
                            ) : (
                              <td className={`px-3 py-2 text-right ${MONO} font-medium text-carbon-ink`}>{formatCurrency(row.QOH_Value)}</td>
                            )}
                          </tr>
                        );
                      })}
                      {(!data?.warehouseSummary || data.warehouseSummary.length === 0) && !loading && (
                        <tr>
                          <td colSpan={viewMode === 'units' ? 6 : 3}>
                            <NoResults onClear={hasActiveFilters ? clearFilters : undefined} />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* By Season */}
              <div className="bg-carbon-white border border-carbon-border overflow-hidden relative">
                <RefetchOverlay />
                <div className="px-[18px] py-2.5 border-b border-carbon-border bg-carbon-gray-10 flex items-center justify-between">
                  <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">On hand by season</span>
                  <button
                    onClick={() => setExcludeAL(!excludeAL)}
                    className={`px-2 py-0.5 font-plex-mono text-[10px] uppercase font-medium border ${
                      excludeAL
                        ? 'bg-carbon-ink text-carbon-white border-carbon-ink'
                        : 'bg-carbon-white text-carbon-gray-70 border-carbon-gray-30 hover:bg-carbon-gray-10'
                    }`}
                  >
                    {excludeAL ? 'AL excluded' : 'Exclude AL'}
                  </button>
                </div>
                <div className="overflow-auto h-96">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 z-5">
                      <tr>
                        <th className={`${TH} text-left`}>Season</th>
                        <th className={`${TH} text-right`}>Styles</th>
                        {viewMode === 'units' ? (
                          <>
                            <th className={`${TH} text-right`}>QOH</th>
                            <th className={`${TH} text-right`}>Open</th>
                            <th className={`${TH} text-right`}>Pick</th>
                            <th className={`${TH} text-right`}>ATS</th>
                          </>
                        ) : (
                          <th className={`${TH} text-right`}>QOH Value</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {data?.seasonSummary?.map((row, idx) => {
                        const isSelected = selectedSeasons.includes(row.Season);
                        return (
                          <tr
                            key={row.Season || idx}
                            onClick={() => handleSeasonClick(row.Season)}
                            className={`border-b border-carbon-gray-10 cursor-pointer ${isSelected ? 'bg-carbon-blue-10' : 'hover:bg-carbon-gray-10'}`}
                          >
                            <td className={`px-3 py-2 ${MONO} font-medium text-carbon-ink`}>{row.Season || '(blank)'}</td>
                            <td className={`px-3 py-2 text-right ${MONO} text-carbon-gray-70`}>{formatUnits(row.Style_Count)}</td>
                            {viewMode === 'units' ? (
                              <>
                                <td className={`px-3 py-2 text-right ${MONO} text-carbon-gray-70`}>{formatUnits(row.Total_QOH)}</td>
                                <td className={`px-3 py-2 text-right ${MONO} text-carbon-gray-70`}>{formatUnits(row.Total_Open)}</td>
                                <td className={`px-3 py-2 text-right ${MONO} text-carbon-gray-70`}>{formatUnits(row.Total_Pick)}</td>
                                <td className={`px-3 py-2 text-right ${MONO} font-medium`} style={{ color: '#24A148' }}>{formatUnits(row.ATS)}</td>
                              </>
                            ) : (
                              <td className={`px-3 py-2 text-right ${MONO} font-medium text-carbon-ink`}>{formatCurrency(row.QOH_Value)}</td>
                            )}
                          </tr>
                        );
                      })}
                      {(!data?.seasonSummary || data.seasonSummary.length === 0) && !loading && (
                        <tr>
                          <td colSpan={viewMode === 'units' ? 6 : 3}>
                            <NoResults onClear={hasActiveFilters ? clearFilters : undefined} />
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Ranked Lists */}
            {data?.seasonSummary?.length > 0 && (() => {
              const valueKey = viewMode === 'dollars' ? 'QOH_Value' : 'Total_QOH';
              const sortedSeasons = [...data.seasonSummary].sort((a, b) => (b[valueKey] || 0) - (a[valueKey] || 0));
              const maxValue = Math.max(...sortedSeasons.map(s => s[valueKey] || 0));
              const count = sortedSeasons.length;
              const gridCols = count <= 3 ? 'grid-cols-1' : count <= 7 ? 'grid-cols-2' : count <= 11 ? 'grid-cols-3' : 'grid-cols-4';

              return (
                <div className="mt-5 bg-carbon-white border border-carbon-border overflow-hidden relative">
                  <RefetchOverlay />
                  <div className="px-[18px] py-2.5 border-b border-carbon-border bg-carbon-gray-10">
                    <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">{viewMode === 'dollars' ? 'QOH value' : 'QOH units'} by season</span>
                  </div>
                  <div className={`p-4 grid ${gridCols} gap-x-6 gap-y-1 max-h-36 overflow-auto`}>
                    {sortedSeasons.map((s, idx) => {
                      const pct = maxValue > 0 ? ((s[valueKey] || 0) / maxValue) * 100 : 0;
                      const isTop10 = idx < 10;
                      return (
                        <div key={idx} className="flex items-center gap-2 text-sm relative py-1">
                          <div
                            className="absolute inset-y-0 left-0 bg-carbon-blue-10"
                            style={{ width: `${pct}%` }}
                          />
                          <span className={`relative z-10 w-6 font-plex-mono text-[11px] font-semibold ${isTop10 ? 'text-carbon-blue-60' : 'text-carbon-gray-40'}`}>
                            {idx + 1}
                          </span>
                          <span className="relative z-10 flex-1 min-w-0 font-plex-mono text-[11.5px] font-medium truncate">{s.Season || '(blank)'}</span>
                          <span className="relative z-10 font-plex-mono text-[11.5px] font-semibold text-carbon-ink shrink-0">
                            {fmt(s[valueKey])}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {data?.customerSummary?.length > 0 && (() => {
              const valueKey = viewMode === 'dollars' ? 'QOH_Value' : 'Total_QOH';
              const maxValue = Math.max(...data.customerSummary.map(c => c[valueKey] || 0));
              const customers = data.customerSummary;
              const count = customers.length;
              const gridCols = count <= 3 ? 'grid-cols-1' : count <= 7 ? 'grid-cols-2' : count <= 11 ? 'grid-cols-3' : 'grid-cols-4';

              return (
                <div className="mt-5 bg-carbon-white border border-carbon-border overflow-hidden relative">
                  <RefetchOverlay />
                  <div className="px-[18px] py-2.5 border-b border-carbon-border bg-carbon-gray-10">
                    <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">{viewMode === 'dollars' ? 'QOH value' : 'QOH units'} by customer</span>
                  </div>
                  <div className={`p-4 grid ${gridCols} gap-x-6 gap-y-1 max-h-36 overflow-auto`}>
                    {customers.map((c, idx) => {
                      const pct = maxValue > 0 ? ((c[valueKey] || 0) / maxValue) * 100 : 0;
                      const isTop10 = idx < 10;
                      return (
                        <div key={idx} className="flex items-center gap-2 text-sm relative py-1">
                          <div
                            className="absolute inset-y-0 left-0 bg-carbon-blue-10"
                            style={{ width: `${pct}%` }}
                          />
                          <span className={`relative z-10 w-6 font-plex-mono text-[11px] font-semibold ${isTop10 ? 'text-carbon-blue-60' : 'text-carbon-gray-40'}`}>
                            {idx + 1}
                          </span>
                          <span className="relative z-10 flex-1 min-w-0 font-plex-mono text-[11.5px] font-medium truncate">{c.Customer}</span>
                          <span className="relative z-10 font-plex-mono text-[11.5px] font-semibold text-carbon-ink shrink-0">
                            {fmt(c[valueKey])}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Detail Table */}
            {data?.detailData?.length > 0 && (
              <div className="mt-5 bg-carbon-white border border-carbon-border overflow-hidden relative">
                <RefetchOverlay />
                <div className="px-[18px] py-2.5 border-b border-carbon-border bg-carbon-gray-10 flex items-center justify-between">
                  <div className="flex items-baseline gap-2.5">
                    <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">Inventory details</span>
                    <span className="font-plex-mono text-[10px] text-carbon-gray-50">{data.detailData.length} ROWS · MAX 500</span>
                  </div>
                  <button
                    onClick={() => {
                      const wb = XLSX.utils.book_new();
                      const ws = XLSX.utils.json_to_sheet(data.detailData);
                      XLSX.utils.book_append_sheet(wb, ws, 'Inventory Details');
                      XLSX.writeFile(wb, `Inventory_Details_${new Date().toISOString().split('T')[0]}.xlsx`);
                    }}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-carbon-white border border-carbon-gray-30 text-carbon-ink hover:bg-carbon-gray-10"
                    title="Export to Excel"
                  >
                    <Download size={12} />
                    Export
                  </button>
                </div>
                <div className="overflow-auto max-h-96">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 z-5">
                      <tr>
                        <th className={`${TH} text-left`}>Style</th>
                        <th className={`${TH} text-left`}>Color</th>
                        <th className={`${TH} text-left`}>Label</th>
                        <th className={`${TH} text-left`}>Season</th>
                        <th className={`${TH} text-left`}>Warehouse</th>
                        <th className={`${TH} text-left`}>Customer</th>
                        <th className={`${TH} text-right`}>QOH</th>
                        <th className={`${TH} text-right`}>Open</th>
                        <th className={`${TH} text-right`}>Pick</th>
                        <th className={`${TH} text-right`}>ATS</th>
                        <th className={`${TH} text-right`}>WAC Cost</th>
                        <th className={`${TH} text-right`}>QOH Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.detailData.map((row, idx) => (
                        <tr key={idx} className="border-b border-carbon-gray-10 hover:bg-carbon-gray-10">
                          <td className={`px-3 py-1.5 ${MONO} font-medium text-carbon-ink`}>{row.Style}</td>
                          <td className="px-3 py-1.5 text-[13px] text-carbon-ink">{row.Color}</td>
                          <td className="px-3 py-1.5 text-[13px] text-carbon-ink">{row.Label}</td>
                          <td className={`px-3 py-1.5 ${MONO} text-carbon-gray-70`}>{row.Season}</td>
                          <td className={`px-3 py-1.5 ${MONO} text-carbon-gray-70`}>{row.Warehouse}</td>
                          <td className="px-3 py-1.5 text-[13px] text-carbon-ink truncate max-w-32">{row.Customer}</td>
                          <td className={`px-3 py-1.5 text-right ${MONO} text-carbon-gray-70`}>{formatUnits(row.QOH)}</td>
                          <td className={`px-3 py-1.5 text-right ${MONO} text-carbon-gray-70`}>{formatUnits(row.Open_Qty)}</td>
                          <td className={`px-3 py-1.5 text-right ${MONO} text-carbon-gray-70`}>{formatUnits(row.Pick_Qty)}</td>
                          <td className={`px-3 py-1.5 text-right ${MONO} font-medium`} style={{ color: '#24A148' }}>{formatUnits(row.ATS)}</td>
                          <td className={`px-3 py-1.5 text-right ${MONO} text-carbon-gray-70`}>${(Number(row.WAC_Cost) || 0).toFixed(2)}</td>
                          <td className={`px-3 py-1.5 text-right ${MONO} font-medium text-carbon-ink`}>{formatCurrency(row.QOH_Value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            </>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* WIP SECTION                                                  */}
        {/* ============================================================ */}
        {activeSection === 'wip' && (
          <div className="p-4">
            {/* KPI strip — per-tile borders */}
            <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] border-l border-b border-carbon-border mb-5 max-w-lg">
              <KpiTile label="Total WIP" value={formatUnits(totals.wip)} color="#0F62FE" />
              <KpiTile label="WIP Value" value={formatCurrency(totals.wipValue)} color="#161616" />
            </div>

            {loading && !data ? (
              <div className="border border-carbon-border">
                <SkeletonRows rows={6} />
              </div>
            ) : (
            <>
            {/* WIP by Season Table */}
            <div className="bg-carbon-white border border-carbon-border overflow-hidden relative">
              <RefetchOverlay />
              <div className="px-[18px] py-2.5 border-b border-carbon-border bg-carbon-gray-10 flex items-center justify-between">
                <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">WIP by season</span>
                <button
                  onClick={() => setExcludeAL(!excludeAL)}
                  className={`px-2 py-0.5 font-plex-mono text-[10px] uppercase font-medium border ${
                    excludeAL
                      ? 'bg-carbon-ink text-carbon-white border-carbon-ink'
                      : 'bg-carbon-white text-carbon-gray-70 border-carbon-gray-30 hover:bg-carbon-gray-10'
                  }`}
                >
                  {excludeAL ? 'AL excluded' : 'Exclude AL'}
                </button>
              </div>
              <div className="overflow-auto max-h-96">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 z-5">
                    <tr>
                      <th className={`${TH} text-left`}>Season</th>
                      <th className={`${TH} text-right`}>Styles</th>
                      {viewMode === 'units' ? (
                        <th className={`${TH} text-right`}>WIP</th>
                      ) : (
                        <th className={`${TH} text-right`}>WIP Value</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {data?.seasonSummary?.filter(row => (row.Total_WIP || 0) > 0 || (row.WIP_Value || 0) > 0).map((row, idx) => {
                      const isSelected = selectedSeasons.includes(row.Season);
                      return (
                        <tr
                          key={row.Season || idx}
                          onClick={() => handleSeasonClick(row.Season)}
                          className={`border-b border-carbon-gray-10 cursor-pointer ${isSelected ? 'bg-carbon-blue-10' : 'hover:bg-carbon-gray-10'}`}
                        >
                          <td className={`px-3 py-2 ${MONO} font-medium text-carbon-ink`}>{row.Season || '(blank)'}</td>
                          <td className={`px-3 py-2 text-right ${MONO} text-carbon-gray-70`}>{formatUnits(row.Style_Count)}</td>
                          {viewMode === 'units' ? (
                            <td className={`px-3 py-2 text-right ${MONO} font-medium`} style={{ color: '#0F62FE' }}>{formatUnits(row.Total_WIP)}</td>
                          ) : (
                            <td className={`px-3 py-2 text-right ${MONO} font-medium text-carbon-ink`}>{formatCurrency(row.WIP_Value)}</td>
                          )}
                        </tr>
                      );
                    })}
                    {(!data?.seasonSummary || data.seasonSummary.filter(r => (r.Total_WIP || 0) > 0).length === 0) && !loading && (
                      <tr>
                        <td colSpan={3}>
                          <NoResults onClear={hasActiveFilters ? clearFilters : undefined} />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* WIP Ranked by Season */}
            {data?.seasonSummary?.length > 0 && (() => {
              const valueKey = viewMode === 'dollars' ? 'WIP_Value' : 'Total_WIP';
              const filtered = data.seasonSummary.filter(s => (s[valueKey] || 0) > 0);
              const sorted = [...filtered].sort((a, b) => (b[valueKey] || 0) - (a[valueKey] || 0));
              const maxValue = Math.max(...sorted.map(s => s[valueKey] || 0), 0);
              const count = sorted.length;
              if (count === 0) return null;
              const gridCols = count <= 3 ? 'grid-cols-1' : count <= 7 ? 'grid-cols-2' : count <= 11 ? 'grid-cols-3' : 'grid-cols-4';

              return (
                <div className="mt-5 bg-carbon-white border border-carbon-border overflow-hidden relative">
                  <RefetchOverlay />
                  <div className="px-[18px] py-2.5 border-b border-carbon-border bg-carbon-gray-10">
                    <span className="font-plex-mono text-[10.5px] tracking-[0.12em] uppercase font-semibold">{viewMode === 'dollars' ? 'WIP value' : 'WIP units'} by season</span>
                  </div>
                  <div className={`p-4 grid ${gridCols} gap-x-6 gap-y-1 max-h-36 overflow-auto`}>
                    {sorted.map((s, idx) => {
                      const pct = maxValue > 0 ? ((s[valueKey] || 0) / maxValue) * 100 : 0;
                      const isTop10 = idx < 10;
                      return (
                        <div key={idx} className="flex items-center gap-2 text-sm relative py-1">
                          <div
                            className="absolute inset-y-0 left-0 bg-carbon-blue-10"
                            style={{ width: `${pct}%` }}
                          />
                          <span className={`relative z-10 w-6 font-plex-mono text-[11px] font-semibold ${isTop10 ? 'text-carbon-blue-60' : 'text-carbon-gray-40'}`}>
                            {idx + 1}
                          </span>
                          <span className="relative z-10 flex-1 min-w-0 font-plex-mono text-[11.5px] font-medium truncate">{s.Season || '(blank)'}</span>
                          <span className="relative z-10 font-plex-mono text-[11.5px] font-semibold text-carbon-ink shrink-0">
                            {fmt(s[valueKey])}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
            </>
            )}
          </div>
        )}

        {/* CHA-369 — Div 60 ATS with images (twin of vw_div60_ats; PBI reads the same view) */}
        {activeSection === 'div60ats' && (
          <div className="p-4">
            <Div60AtsReport />
          </div>
        )}
      </div>

      {/* Module footnote */}
      <div className="flex gap-2 items-start mt-3.5 text-carbon-gray-50 text-xs leading-relaxed max-w-[680px]">
        <Boxes size={14} className="mt-0.5 flex-shrink-0" />
        <span>On hand &amp; WIP by warehouse and season, from the daily ERP sync. Row clicks toggle warehouse/season filters; the details grid (and its Excel export) is capped at 500 rows.</span>
      </div>
    </div>
  );
}
